'use client'

import {
  ExternalLink,
  Loader2,
  Maximize2,
  Minimize2,
  Play,
  RotateCcw,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

type PlayPanelProps = {
  iframeSrc: string
  iframeTitle: string
  note: string
  playLabel: string
  loadHint: string
  loadingLabel: string
  fullscreenLabel: string
  resetLabel: string
  delayedLabel: string
  errorLabel: string
  officialLabel: string
  officialUrl: string
}

export function PlayPanel({
  iframeSrc,
  iframeTitle,
  note,
  playLabel,
  loadHint,
  loadingLabel,
  fullscreenLabel,
  resetLabel,
  delayedLabel,
  errorLabel,
  officialLabel,
  officialUrl,
}: PlayPanelProps) {
  const [shouldLoad, setShouldLoad] = useState(false)
  const [iframeKey, setIframeKey] = useState(0)
  const [frameStatus, setFrameStatus] = useState<
    'idle' | 'loading' | 'loaded' | 'slow' | 'error'
  >('idle')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const frameRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (frameStatus !== 'loading') return

    // 跨域窗口的加载事件无法确认游戏资源就绪，超时后提供可操作的备用入口。
    const timeout = window.setTimeout(() => setFrameStatus('slow'), 20_000)
    return () => window.clearTimeout(timeout)
  }, [frameStatus, iframeKey])

  useEffect(() => {
    const handleFullscreenChange = () => {
      const target = frameRef.current
      setIsFullscreen(document.fullscreenElement === target)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const startPlaying = () => {
    if (shouldLoad) return
    setShouldLoad(true)
    setFrameStatus('loading')
    setIframeKey((key) => key + 1)
  }

  const resetFrame = () => {
    if (!shouldLoad) return
    setFrameStatus('loading')
    setIframeKey((key) => key + 1)
  }

  const toggleFullscreen = async () => {
    const target = frameRef.current
    if (!target) return

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
        setIsFullscreen(false)
      } else {
        await target.requestFullscreen()
        setIsFullscreen(true)
      }
    } catch (error) {
      console.error('Fullscreen toggle failed', error)
    }
  }

  return (
    <div
      ref={frameRef}
      className="relative isolate flex min-h-full flex-col overflow-hidden rounded-xl border border-white/15 bg-gradient-to-br from-[#09070b] via-[#12080f] to-[#2a0b12] text-slate-50 shadow-2xl shadow-black/60 ring-1 ring-white/10 backdrop-blur sm:rounded-2xl md:rounded-[28px]"
    >
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(190,24,93,0.25),transparent_40%),radial-gradient(circle_at_85%_10%,rgba(250,204,21,0.18),transparent_35%),radial-gradient(circle_at_50%_85%,rgba(14,116,144,0.2),transparent_45%)]" />
        <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(130deg,rgba(255,255,255,0.08)_0,transparent_45%)]" />
        <div className="absolute inset-3 rounded-lg border border-white/10 sm:inset-4 sm:rounded-xl md:inset-6 md:rounded-[24px]" />
      </div>

      <div
        className={`relative flex min-h-[250px] flex-1 flex-col justify-center overflow-hidden sm:min-h-[340px] md:min-h-[440px] ${
          shouldLoad ? 'aspect-[16/9]' : 'md:aspect-[16/9]'
        }`}
      >
        {shouldLoad ? (
          <>
            <iframe
              key={iframeKey}
              src={iframeSrc}
              title={iframeTitle}
              className="absolute inset-0 h-full w-full border-0"
              allowFullScreen
              loading="eager"
              onLoad={() => setFrameStatus('loaded')}
              onError={() => setFrameStatus('error')}
            />
            {frameStatus === 'loading' && (
              <div
                role="status"
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-950/70 backdrop-blur-sm"
              >
                <Loader2 className="h-8 w-8 animate-spin text-amber-200" />
                <p className="text-sm font-medium text-slate-100">
                  {loadingLabel}
                </p>
              </div>
            )}
            {frameStatus === 'slow' || frameStatus === 'error' ? (
              <div
                role="status"
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-slate-950/95 px-5 text-center"
              >
                <p className="max-w-md text-sm leading-6 text-slate-100">
                  {frameStatus === 'error' ? errorLabel : delayedLabel}
                </p>
                <div className="flex flex-wrap justify-center gap-3 text-sm">
                  <button
                    type="button"
                    onClick={resetFrame}
                    className="rounded-lg border border-amber-200/40 bg-amber-200/10 px-4 py-2 text-amber-100 hover:bg-amber-200/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-200"
                  >
                    {resetLabel}
                  </button>
                  <a
                    href={officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-200"
                  >
                    {officialLabel}
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="relative z-10 flex w-full flex-1 items-center justify-center px-4 py-12 md:absolute md:inset-0 md:px-6 md:py-0">
            <div className="flex max-w-lg flex-col items-center gap-4 rounded-2xl bg-white/5 p-5 text-center shadow-lg shadow-sky-950/30 backdrop-blur-lg md:max-w-xl md:gap-6 md:p-10">
              <button
                type="button"
                onClick={startPlaying}
                className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-amber-200 via-amber-300 to-rose-300 px-5 py-2.5 text-base font-semibold text-slate-900 shadow-lg shadow-amber-300/30 transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-200 md:px-6 md:py-3 md:text-lg"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-rose-700 transition group-hover:scale-105 md:h-11 md:w-11">
                  <Play className="h-4 w-4 md:h-5 md:w-5" fill="currentColor" />
                </span>
                <span>{playLabel}</span>
              </button>
              <p className="text-sm text-slate-100/80">{loadHint}</p>
              {/* <p className="text-xs text-slate-100/60">{note}</p> */}
            </div>
          </div>
        )}
      </div>

      <div className="relative mt-auto flex shrink-0 flex-wrap items-center justify-between gap-4 border-t border-white/10 bg-black/40 px-4 py-3 text-xs text-slate-100 md:px-6 md:py-4 md:text-sm">
        <p className="max-w-3xl text-left text-slate-200/80">{note}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetFrame}
            disabled={!shouldLoad}
            aria-label={resetLabel}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 text-slate-100 transition hover:-translate-y-0.5 hover:border-amber-200 hover:bg-amber-200/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 md:h-11 md:w-11"
            title={resetLabel}
          >
            <RotateCcw className="h-4 w-4 md:h-5 md:w-5" />
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            disabled={!shouldLoad}
            aria-label={fullscreenLabel}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 text-slate-100 transition hover:-translate-y-0.5 hover:border-amber-200 hover:bg-amber-200/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 md:h-11 md:w-11"
            title={fullscreenLabel}
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4 md:h-5 md:w-5" />
            ) : (
              <Maximize2 className="h-4 w-4 md:h-5 md:w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
